import React, { useState } from 'react';
import {
  X,
  Edit2,
  Save,
  User,
  Mail,
  Phone,
  Building,
  FileText,
  MapPin,
  DollarSign,
  TrendingUp,
  Award,
  Users2,
  Briefcase,
  Zap,
  Target,
  Calendar,
  AlertCircle,
  CheckCircle,
  Globe
} from 'lucide-react';

const AddEditCandidateModal = ({ candidate, onSave, onClose, isEdit = false }) => {
  const [formData, setFormData] = useState({
    plcCode: candidate?.plcCode || '',
    sapId: candidate?.sapId || '',
    candidateName: candidate?.candidateName || '',
    candidatePhone: candidate?.candidatePhone || '',
    candidateEmail: candidate?.candidateEmail || '',
    linkedinUrl: candidate?.linkedinUrl || '',
    certifications: candidate?.certifications || [],
    workEmail: candidate?.workEmail || '',
    reportingManagerName: candidate?.reportingManagerName || '',
    reportingManagerEmail: candidate?.reportingManagerEmail || '',
    client: candidate?.client || '',
    clientTrack: candidate?.clientTrack || '',
    endClient: candidate?.endClient || '',
    jobTitle: candidate?.jobTitle || '',
    primarySkills: candidate?.primarySkills || '',
    secondarySkills: candidate?.secondarySkills || '',
    startDate: candidate?.startDate || '',
    endDate: candidate?.endDate || '',
    sowEndDate: candidate?.sowEndDate || '',
    sowEndMonth: candidate?.sowEndMonth || '',
    extendedSowDate: candidate?.extendedSowDate || '',
    contractorStatus: candidate?.contractorStatus || 'C2C',
    projectCity: candidate?.projectCity || '',
    projectState: candidate?.projectState || '',
    projectCountry: candidate?.projectCountry || 'USA',
    payRateCurrency: candidate?.payRateCurrency || 'USD',
    payRate: candidate?.payRate || '',
    payRatePaymentBasis: candidate?.payRatePaymentBasis || 'Hourly',
    clientRateCurrency: candidate?.clientRateCurrency || 'USD',
    clientRate: candidate?.clientRate || '',
    clientRatePaymentBasis: candidate?.clientRatePaymentBasis || 'Hourly',
    marginValue: candidate?.marginValue || '',
    contractorVendorName: candidate?.contractorVendorName || '',
    vendorContactPerson: candidate?.vendorContactPerson || '',
    vendorContactNumber: candidate?.vendorContactNumber || '',
    vendorContactEmail: candidate?.vendorContactEmail || '',
    dalName: candidate?.dalName || '',
    dmName: candidate?.dmName || '',
    addName: candidate?.addName || '',
    buHead: candidate?.buHead || '',
    clientProjectManager: candidate?.clientProjectManager || '',
    clientResourceManager: candidate?.clientResourceManager || '',
    account_manager_1: candidate?.account_manager_1 || '',
    account_manager_2: candidate?.account_manager_2 || '',
    account_manager_3: candidate?.account_manager_3 || '',
    account_manager_4: candidate?.account_manager_4 || '',
    account_manager_5: candidate?.account_manager_5 || '',
    account_manager_6: candidate?.account_manager_6 || '',
    account_manager_7: candidate?.account_manager_7 || '',
    account_manager_8: candidate?.account_manager_8 || '',
    dob: candidate?.dob || '',
    calName: candidate?.calName || '',
    passthroughOwner: candidate?.passthroughOwner || '',
    passthroughSupport: candidate?.passthroughSupport || '',
    leadRecruiter: candidate?.leadRecruiter || '',
    teamLeadName: candidate?.teamLeadName || '',
    recruiterName: candidate?.recruiterName || '',
    recruiterEmpId: candidate?.recruiterEmpId || '',
    dealPtPtr: candidate?.dealPtPtr || '',
    status: candidate?.status || 'Active'
  });

  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.candidateName.trim()) newErrors.candidateName = 'Name is required';
      if (!formData.candidateEmail.trim()) newErrors.candidateEmail = 'Email is required';
      if (formData.candidateEmail && !/\S+@\S+\.\S+/.test(formData.candidateEmail)) {
        newErrors.candidateEmail = 'Invalid email format';
      }
      if (!formData.candidatePhone.trim()) newErrors.candidatePhone = 'Phone is required';
      if (!formData.plcCode.trim()) newErrors.plcCode = 'PLC Code is required';
      if (!formData.sapId.trim()) newErrors.sapId = 'SAP ID is required';
    } else if (step === 2) {
      if (!formData.client.trim()) newErrors.client = 'Client is required';
      if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job Title is required';
      if (!formData.primarySkills.trim()) newErrors.primarySkills = 'Primary Skills are required';
      if (!formData.startDate) newErrors.startDate = 'Start Date is required';
      if (!formData.projectCity.trim()) newErrors.projectCity = 'Project City is required';
      if (!formData.projectState.trim()) newErrors.projectState = 'Project State is required';
      if (!formData.sowEndDate) newErrors.sowEndDate = 'SOW End Date is required';
    } else if (step === 3) {
      if (!formData.payRate) newErrors.payRate = 'Pay Rate is required';
      if (!formData.clientRate) newErrors.clientRate = 'Client Rate is required';
      if (!formData.contractorVendorName.trim()) newErrors.contractorVendorName = 'Vendor Name is required';
      if (!formData.dalName.trim()) newErrors.dalName = 'DAL Name is required';
      if (!formData.dmName.trim()) newErrors.dmName = 'DM Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    if (field === 'payRate' || field === 'clientRate') {
      const payRate = field === 'payRate' ? parseFloat(value) : parseFloat(formData.payRate);
      const clientRate = field === 'clientRate' ? parseFloat(value) : parseFloat(formData.clientRate);
      
      if (payRate && clientRate && clientRate > 0) {
        const margin = ((clientRate - payRate) / clientRate * 100).toFixed(1);
        setFormData(prev => ({
          ...prev,
          marginValue: margin
        }));
      }
    }

    if (field === 'sowEndDate') {
      setFormData(prev => ({
        ...prev,
        sowEndMonth: value ? new Date(value).toLocaleString('default', { month: 'long', year: 'numeric' }) : ''
      }));
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
  if (validateStep(currentStep)) {
    // Prepare certifications data
    const preparedFormData = {
      ...formData,
      certifications: Array.isArray(formData.certifications)
        ? formData.certifications
        : [],
    };

    // Optional: Only show warning if end date is set AND it's in the past
    if (preparedFormData.endDate) {
      const endDate = new Date(preparedFormData.endDate);
      const today = new Date();

      if (endDate < today && !candidate?.endDate) {
        const confirmed = window.confirm(
          `⚠️ NOTICE: The end date you've set is in the past. This candidate will be marked as INACTIVE.\n\nCandidate: ${preparedFormData.candidateName}\nEnd Date: ${preparedFormData.endDate}\n\nDo you want to continue?`
        );

        if (!confirmed) {
          return;
        }

        const finalFormData = {
          ...preparedFormData,
          status: "Inactive",
          // ✅ Prevent extension_status from being auto-updated on general edits
          preventExtensionStatusUpdate: true,
          enableExtensionStatusCalc: false,
          enableRDForwarding: false,
          enableFeedbackTracking: false,
        };

        if (isEdit) {
          onSave(candidate.id, finalFormData);
        } else {
          onSave(finalFormData);
        }
        return;
      }
    }

    // Normal save without status change
    if (isEdit) {
      const cleanFormData = Object.fromEntries(
        Object.entries(preparedFormData).filter(
          ([key, value]) =>
            value !== "" && value !== null && value !== undefined
        )
      );

      // ✅ Always add these flags for general edits
      onSave(candidate.id, {
        ...cleanFormData,
        preventExtensionStatusUpdate: true,
        enableExtensionStatusCalc: false,
        enableRDForwarding: false,
        enableFeedbackTracking: false,
      });
    } else {
      onSave(preparedFormData);
    }
  }
};

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Personal & Contact Information</h3>
        </div>
        <p className="text-slate-600 text-sm">Enter the candidate's basic details and contact information</p>
      </div>

      {/* Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <User className="w-4 h-4 mr-2 text-blue-600" />
            Candidate Name *
          </label>
          <input
            type="text"
            value={formData.candidateName}
            onChange={(e) => handleInputChange('candidateName', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
              errors.candidateName ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="Enter full name"
          />
          {errors.candidateName && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.candidateName}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Mail className="w-4 h-4 mr-2 text-blue-600" />
            Email Address *
          </label>
          <input
            type="email"
            value={formData.candidateEmail}
            onChange={(e) => handleInputChange('candidateEmail', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
              errors.candidateEmail ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="john@email.com"
          />
          {errors.candidateEmail && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.candidateEmail}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Phone className="w-4 h-4 mr-2 text-blue-600" />
            Phone Number *
          </label>
          <input
            type="tel"
            value={formData.candidatePhone}
            onChange={(e) => handleInputChange('candidatePhone', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
              errors.candidatePhone ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="+1-555-0123"
          />
          {errors.candidatePhone && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.candidatePhone}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Globe className="w-4 h-4 mr-2 text-blue-600" />
            LinkedIn Profile URL
          </label>
          <input
            type="url"
            value={formData.linkedinUrl}
            onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-400 transition-all duration-200"
            placeholder="https://linkedin.com/in/username"
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Calendar className="w-4 h-4 mr-2 text-blue-600" />
            Date of Birth
          </label>
          <input
            type="date"
            value={formData.dob}
            onChange={(e) => handleInputChange('dob', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-400 transition-all duration-200"
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Building className="w-4 h-4 mr-2 text-green-600" />
            Work Email
          </label>
          <input
            type="email"
            value={formData.workEmail}
            onChange={(e) => handleInputChange('workEmail', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
            placeholder="john@company.com"
          />
        </div>
      </div>

      {/* ID Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-purple-600" />
          Identification Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">PLC Code *</label>
            <input
              type="text"
              value={formData.plcCode}
              onChange={(e) => handleInputChange('plcCode', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 ${
                errors.plcCode ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              placeholder="PLC001"
            />
            {errors.plcCode && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.plcCode}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">SAP ID *</label>
            <input
              type="text"
              value={formData.sapId}
              onChange={(e) => handleInputChange('sapId', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 ${
                errors.sapId ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              placeholder="SAP001"
            />
            {errors.sapId && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.sapId}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reporting Manager Section */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-2xl border border-emerald-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <Users2 className="w-5 h-5 mr-2 text-emerald-600" />
          Reporting Structure (Optional)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Manager Name</label>
            <input
              type="text"
              value={formData.reportingManagerName}
              onChange={(e) => handleInputChange('reportingManagerName', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Manager Name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Manager Email</label>
            <input
              type="email"
              value={formData.reportingManagerEmail}
              onChange={(e) => handleInputChange('reportingManagerEmail', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 hover:border-slate-400 transition-all duration-200"
              placeholder="manager@company.com"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Project & Skills Information</h3>
        </div>
        <p className="text-slate-600 text-sm">Project details, role information, and technical skills</p>
      </div>

      {/* Client Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Building className="w-4 h-4 mr-2 text-green-600" />
            Client *
          </label>
          <input
            type="text"
            value={formData.client}
            onChange={(e) => handleInputChange('client', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
              errors.client ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="Microsoft"
          />
          {errors.client && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.client}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Target className="w-4 h-4 mr-2 text-green-600" />
            Client Track
          </label>
          <input
            type="text"
            value={formData.clientTrack}
            onChange={(e) => handleInputChange('clientTrack', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
            placeholder="Azure Cloud Track"
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Building className="w-4 h-4 mr-2 text-green-600" />
            End Client
          </label>
          <input
            type="text"
            value={formData.endClient}
            onChange={(e) => handleInputChange('endClient', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
            placeholder="End Client Name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
          <Briefcase className="w-4 h-4 mr-2 text-green-600" />
          Job Title *
        </label>
        <input
          type="text"
          value={formData.jobTitle}
          onChange={(e) => handleInputChange('jobTitle', e.target.value)}
          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
            errors.jobTitle ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
          }`}
          placeholder="Senior Developer"
        />
        {errors.jobTitle && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.jobTitle}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Zap className="w-4 h-4 mr-2 text-green-600" />
            Primary Skills *
          </label>
          <textarea
            value={formData.primarySkills}
            onChange={(e) => handleInputChange('primarySkills', e.target.value)}
            rows={3}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
              errors.primarySkills ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="React, Node.js, AWS"
          />
          {errors.primarySkills && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.primarySkills}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Target className="w-4 h-4 mr-2 text-green-600" />
            Secondary Skills
          </label>
          <textarea
            value={formData.secondarySkills}
            onChange={(e) => handleInputChange('secondarySkills', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
            placeholder="Python, Docker, Kubernetes"
          />
        </div>
      </div>

      {/* Certifications Section */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-2xl border border-amber-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <Award className="w-5 h-5 mr-2 text-amber-600" />
          Professional Certifications
        </h4>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-700">
            Certifications (comma-separated)
          </label>
          <textarea
            value={Array.isArray(formData.certifications) ? formData.certifications.join(', ') : formData.certifications}
            onChange={(e) => {
              const value = e.target.value;
              const certArray = value.split(',').map(cert => cert.trim()).filter(cert => cert !== '');
              handleInputChange('certifications', certArray);
            }}
            rows={3}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 hover:border-slate-400 transition-all duration-200"
            placeholder="AWS Certified Solutions Architect, PMP Certification, Scrum Master"
          />
          <p className="text-xs text-slate-500 mt-1">
            Enter certifications separated by commas. Example: AWS Certified, PMP, Scrum Master
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Calendar className="w-4 h-4 mr-2 text-green-600" />
            Start Date *
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
              errors.startDate ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
          />
          {errors.startDate && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.startDate}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Calendar className="w-4 h-4 mr-2 text-green-600" />
            End Date
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Calendar className="w-4 h-4 mr-2 text-green-600" />
            SOW End Date *
          </label>
          <input
            type="date"
            value={formData.sowEndDate}
            onChange={(e) => handleInputChange('sowEndDate', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
              errors.sowEndDate ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
          />
          {errors.sowEndDate && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.sowEndDate}</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <MapPin className="w-4 h-4 mr-2 text-green-600" />
            Project City *
          </label>
          <input
            type="text"
            value={formData.projectCity}
            onChange={(e) => handleInputChange('projectCity', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
              errors.projectCity ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="Seattle"
          />
          {errors.projectCity && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.projectCity}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <MapPin className="w-4 h-4 mr-2 text-green-600" />
            Project State *
          </label>
          <input
            type="text"
            value={formData.projectState}
            onChange={(e) => handleInputChange('projectState', e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
              errors.projectState ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="WA"
          />
          {errors.projectState && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.projectState}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <Globe className="w-4 h-4 mr-2 text-green-600" />
            Project Country
          </label>
          <select
            value={formData.projectCountry}
            onChange={(e) => handleInputChange('projectCountry', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
          >
            <option value="USA">USA</option>
            <option value="Canada">Canada</option>
            <option value="India">India</option>
            <option value="UK">UK</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
            <FileText className="w-4 h-4 mr-2 text-green-600" />
            Contractor Status
          </label>
          <select
            value={formData.contractorStatus}
            onChange={(e) => handleInputChange('contractorStatus', e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
          >
            <option value="C2C">C2C</option>
            <option value="W2">W2</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl border border-yellow-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Financial & Team Structure</h3>
        </div>
        <p className="text-slate-600 text-sm">Contract rates, vendor details, and team assignments</p>
      </div>

      {/* Pay Rate Section */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-2xl border border-blue-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
          Payment Structure
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Pay Rate Currency</label>
            <select
              value={formData.payRateCurrency}
              onChange={(e) => handleInputChange('payRateCurrency', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-400 transition-all duration-200"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Pay Rate *</label>
            <input
              type="number"
              value={formData.payRate}
              onChange={(e) => handleInputChange('payRate', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
                errors.payRate ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              placeholder="125"
            />
            {errors.payRate && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.payRate}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Pay Rate Payment Basis</label>
            <select
              value={formData.payRatePaymentBasis}
              onChange={(e) => handleInputChange('payRatePaymentBasis', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 hover:border-slate-400 transition-all duration-200"
            >
              <option value="Hourly">Hourly</option>
              <option value="Daily">Daily</option>
              <option value="Monthly">Monthly</option>
              <option value="Annual">Annual</option>
            </select>
          </div>
        </div>
      </div>

      {/* Client Rate Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
          Client Billing Structure
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Client Rate Currency</label>
            <select
              value={formData.clientRateCurrency}
              onChange={(e) => handleInputChange('clientRateCurrency', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Client Rate *</label>
            <input
              type="number"
              value={formData.clientRate}
              onChange={(e) => handleInputChange('clientRate', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 ${
                errors.clientRate ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              placeholder="180"
            />
            {errors.clientRate && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.clientRate}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Client Rate Payment Basis</label>
            <select
              value={formData.clientRatePaymentBasis}
              onChange={(e) => handleInputChange('clientRatePaymentBasis', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200"
            >
              <option value="Hourly">Hourly</option>
              <option value="Daily">Daily</option>
              <option value="Monthly">Monthly</option>
              <option value="Annual">Annual</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Margin (%)</label>
            <input
              type="number"
              value={formData.marginValue}
              onChange={(e) => handleInputChange('marginValue', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200 bg-slate-50"
              placeholder="30.6"
              readOnly
            />
            <p className="text-xs text-slate-500">Auto-calculated</p>
          </div>
        </div>
      </div>

      {/* Vendor Information */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <Building className="w-5 h-5 mr-2 text-purple-600" />
          Vendor Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Contractor/Vendor Name *</label>
            <input
              type="text"
              value={formData.contractorVendorName}
              onChange={(e) => handleInputChange('contractorVendorName', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 ${
                errors.contractorVendorName ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              placeholder="TechElite Solutions"
            />
            {errors.contractorVendorName && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.contractorVendorName}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Vendor Contact Person</label>
            <input
              type="text"
              value={formData.vendorContactPerson}
              onChange={(e) => handleInputChange('vendorContactPerson', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 hover:border-slate-400 transition-all duration-200"
              placeholder="John Smith"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Vendor Contact Number</label>
            <input
              type="tel"
              value={formData.vendorContactNumber}
              onChange={(e) => handleInputChange('vendorContactNumber', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 hover:border-slate-400 transition-all duration-200"
              placeholder="+1-555-0123"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Vendor Contact Email ID</label>
            <input
              type="email"
              value={formData.vendorContactEmail}
              onChange={(e) => handleInputChange('vendorContactEmail', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 hover:border-slate-400 transition-all duration-200"
              placeholder="contact@vendor.com"
            />
          </div>
        </div>
      </div>

      {/* Internal Team Structure */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-2xl border border-orange-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <Users2 className="w-5 h-5 mr-2 text-orange-600" />
          Internal Team Structure
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Delivery Account Lead (DAL) Name *</label>
            <input
              type="text"
              value={formData.dalName}
              onChange={(e) => handleInputChange('dalName', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 ${
                errors.dalName ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              placeholder="Sarah Johnson"
            />
            {errors.dalName && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.dalName}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Delivery Manager (DM) Name *</label>
            <input
              type="text"
              value={formData.dmName}
              onChange={(e) => handleInputChange('dmName', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 ${
                errors.dmName ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              placeholder="Michael Chen"
            />
            {errors.dmName && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.dmName}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Associate Director Delivery (ADD) Name</label>
            <input
              type="text"
              value={formData.addName}
              onChange={(e) => handleInputChange('addName', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Jennifer Davis"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">BU Head</label>
            <input
              type="text"
              value={formData.buHead}
              onChange={(e) => handleInputChange('buHead', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Robert Wilson"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Client Project Manager</label>
            <input
              type="text"
              value={formData.clientProjectManager}
              onChange={(e) => handleInputChange('clientProjectManager', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Alex Thompson"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Client Resource Manager Name</label>
            <input
              type="text"
              value={formData.clientResourceManager}
              onChange={(e) => handleInputChange('clientResourceManager', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Lisa Garcia"
            />
          </div>
        </div>
      </div>

      {/* Account Managers Section */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <Mail className="w-5 h-5 mr-2 text-blue-600" />
          Account Managers
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
            <div key={num} className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Account Manager {num}</label>
              <input
                type="email"
                value={formData[`account_manager_${num}`]}
                onChange={(e) => handleInputChange(`account_manager_${num}`, e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-all duration-200"
                placeholder="manager@company.com"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Recruitment & Hierarchy Structure */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 p-6 rounded-2xl border border-indigo-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <Users2 className="w-5 h-5 mr-2 text-indigo-600" />
          Recruitment & Hierarchy Structure
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">CAL Name</label>
            <input
              type="text"
              value={formData.calName}
              onChange={(e) => handleInputChange('calName', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Client Account Lead Name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Passthrough Owner</label>
            <input
              type="text"
              value={formData.passthroughOwner}
              onChange={(e) => handleInputChange('passthroughOwner', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Passthrough Owner"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Passthrough Support</label>
            <input
              type="text"
              value={formData.passthroughSupport}
              onChange={(e) => handleInputChange('passthroughSupport', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Passthrough Support"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Lead Recruiter</label>
            <input
              type="text"
              value={formData.leadRecruiter}
              onChange={(e) => handleInputChange('leadRecruiter', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Lead Recruiter Name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Team Lead Name</label>
            <input
              type="text"
              value={formData.teamLeadName}
              onChange={(e) => handleInputChange('teamLeadName', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Team Lead Name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Recruiter Name</label>
            <input
              type="text"
              value={formData.recruiterName}
              onChange={(e) => handleInputChange('recruiterName', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Recruiter Name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Recruiter Employee ID</label>
            <input
              type="text"
              value={formData.recruiterEmpId}
              onChange={(e) => handleInputChange('recruiterEmpId', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="EMP001"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Deal Point Pointer</label>
            <input
              type="text"
              value={formData.dealPtPtr}
              onChange={(e) => handleInputChange('dealPtPtr', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-slate-400 transition-all duration-200"
              placeholder="Deal Point Pointer"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden border border-slate-200">
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900 px-8 py-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Edit2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {isEdit ? 'Edit Candidate Profile' : 'Add New Candidate'}
                </h2>
                <p className="text-blue-100 text-sm">
                  Step {currentStep} of 3 - {currentStep === 1 ? 'Personal & Contact Details' : currentStep === 2 ? 'Project & Skills Information' : 'Financial & Team Structure'}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-3 hover:bg-white/20 rounded-xl transition-all duration-200 group"
            >
              <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="px-8 py-6 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-6">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                    currentStep >= step 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-110' 
                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}>
                    {currentStep > step ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      step
                    )}
                    {currentStep >= step && (
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 animate-pulse opacity-50"></div>
                    )}
                  </div>
                  {step < 3 && (
                    <div className={`w-16 h-1 mx-3 rounded-full transition-all duration-500 ${
                      currentStep > step ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-slate-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-sm text-slate-600 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm">
              Progress: {Math.round((currentStep / 3) * 100)}%
            </div>
          </div>
          
          {/* Step Labels */}
          <div className="flex justify-between text-xs text-slate-600 mt-2">
            <span className={currentStep === 1 ? 'font-semibold text-blue-600' : ''}>Personal Details</span>
            <span className={currentStep === 2 ? 'font-semibold text-blue-600' : ''}>Project Information</span>
            <span className={currentStep === 3 ? 'font-semibold text-blue-600' : ''}>Contract & Team</span>
          </div>
        </div>

        <div className="p-8 max-h-[calc(95vh-300px)] overflow-y-auto">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          <div className="flex justify-between items-center pt-8 border-t border-slate-200 mt-8">
            <button
              onClick={onClose}
              className="px-6 py-3 text-slate-700 border-2 border-slate-300 rounded-xl hover:bg-slate-50 transition-all duration-200 font-medium"
            >
              Cancel
            </button>

            <div className="flex space-x-4">
              {currentStep > 1 && (
                <button
                  onClick={handlePrevious}
                  className="px-6 py-3 text-slate-700 border-2 border-slate-300 rounded-xl hover:bg-slate-50 transition-all duration-200 font-medium"
                >
                  Previous
                </button>
              )}

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
                >
                  Next Step
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <Save className="w-5 h-5" />
                  <span>{isEdit ? 'Update Candidate' : 'Add Candidate'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditCandidateModal;